import React from 'react';
import { SimpleSEO } from '@/components/seo/SimpleSEO';
import { RotatingRingAvatar } from '@/components/ui/rotating-ring-avatar';
import { createBlogSEO } from '@/utils/blogSeo';
import { Rocket, CheckCircle, Coins, Clock, Shield, Zap } from 'lucide-react';

export const SolanaTokenLauncher: React.FC = () => {
  const seo = createBlogSEO(
    "Create SPL Tokens on Solana - Complete Guide",
    "Step-by-step guide for creating SPL tokens on Solana blockchain. Design, mint, and deploy custom crypto tokens and meme coins with metadata—no coding required.",
    [
      "SPL token",
      "Solana",
      "create token",
      "token generator",
      "meme coin",
      "crypto token",
      "token creation",
      "SPL generator",
      "token launcher",
      "Solana blockchain"
    ]
  );

  const howToStructuredData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Create SPL Tokens on Solana: Complete Guide 2025",
    "description": "Learn how to create your own SPL tokens on Solana blockchain. Step-by-step guide for token creation, deployment, and verification. Create custom crypto tokens and meme coins with metadata—no coding required.",
    "datePublished": "2025-03-27",
    "dateModified": "2025-03-27",
    "author": {
      "@type": "Person",
      "name": "Daniel T.",
      "url": "https://mdbapp.io/authors/dtdevmdbappio"
    },
    "step": [
      {
        "@type": "HowToStep",
        "name": "Preparation",
        "text": "Set up wallet and gather token requirements"
      },
      {
        "@type": "HowToStep",
        "name": "Token Creation",
        "text": "Create and deploy SPL token on Solana blockchain"
      },
      {
        "@type": "HowToStep",
        "name": "Metadata Setup",
        "text": "Add logo and metadata to your SPL token"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "Solana Wallet",
        "description": "Phantom, Solflare, or other Solana-compatible wallet"
      },
      {
        "@type": "HowToTool",
        "name": "Solana Token Launcher",
        "description": "User-friendly SPL token creation interface"
      }
    ]
  };

  return (
    <>
      <SimpleSEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />

      <div className="max-w-4xl mx-auto space-y-8">
        <article className="prose prose-gray dark:prose-invert max-w-none">
          <header className="space-y-4">
            <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-green-400">
              Create SPL Tokens on Solana Blockchain
            </h1>
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <RotatingRingAvatar
                src="/authors/dtdevmdbappio.jpg"
                alt="Daniel T."
                fromColor="#9333ea"
                toColor="#4ade80"
                onError={(e) => {
                  e.currentTarget.src = 'https://api.dicebear.com/7.x/initials/svg?seed=DT&backgroundColor=0a74ff';
                }}
              />
              <div>
                <p className="font-medium">Daniel T. (MDB Dev)</p>
                <p className="text-sm">Published on March 27, 2025</p>
              </div>
            </div>

            <div className="flex justify-center my-6">
              <a 
                href="https://solanatokenlauncher.app?ref=7tkTC9W3fZWvYcUyDvBtDnS3LKW3YsVij89Qkd2q2UcK"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block hover:opacity-90 transition-opacity"
              >
                <img 
                  src="/logo512.png" 
                  alt="Solana Token Launcher App" 
                  className="h-24 w-auto rounded-lg animate-spin-slow"
                />
              </a>
            </div>
            
            <div className="text-center">
              <a 
                href="https://solanatokenlauncher.app?ref=7tkTC9W3fZWvYcUyDvBtDnS3LKW3YsVij89Qkd2q2UcK"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-purple-400 to-green-400 hover:from-purple-700 hover:via-purple-500 hover:to-green-500 transition-colors"
              >
                Skip the Blog & Launch some tokens now on SolanaTokenLauncher.App
              </a>
            </div>
          </header>

          <h1 className="text-4xl font-bold mb-4">{seo.title}</h1>
          <p className="text-muted-foreground">Published on March 27, 2025 • Last Updated March 27, 2025 • 8 min read</p>

          <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg mt-4">
            <p className="font-semibold text-gray-900 dark:text-gray-100">Quick Navigation:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2 text-gray-700 dark:text-gray-300">
              <li><a href="#introduction">Introduction to SPL Tokens</a></li>
              <li><a href="#prerequisites">Prerequisites & Setup</a></li>
              <li><a href="#token-creation">Token Creation Process</a></li>
              <li><a href="#metadata">Adding Metadata & Logo</a></li>
              <li><a href="#referral-system">Referral System</a></li>
              <li><a href="#best-practices">Best Practices</a></li>
            </ul>
          </div>

          <h2 id="introduction" className="text-2xl font-semibold mt-8 mb-4">Introduction to SPL Tokens</h2>
          <p>
            SPL tokens (Solana Program Library tokens) are the standard token format on the Solana blockchain. 
            Similar to ERC-20 tokens on Ethereum, SPL tokens allow you to create custom cryptocurrencies, 
            meme coins, and digital assets on Solana's high-performance blockchain.
          </p>
          <p>
            Creating SPL tokens offers several advantages:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Fast Transactions:</strong> Solana's high throughput enables near-instant transactions
            </li>
            <li>
              <strong>Low Fees:</strong> Minimal transaction costs compared to Ethereum
            </li>
            <li>
              <strong>Scalability:</strong> Solana can handle thousands of transactions per second
            </li>
            <li>
              <strong>Rich Metadata:</strong> Support for token metadata including images and attributes
            </li>
          </ul>

          <h2 id="prerequisites" className="text-2xl font-semibold mt-8 mb-4">Prerequisites & Setup</h2>
          <p>
            Before creating your SPL token on Solana, ensure you have the following ready:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Solana Wallet:</strong> Install a Solana-compatible wallet like Phantom or Solflare
            </li>
            <li>
              <strong>SOL Tokens:</strong> You'll need a small amount of SOL for transaction fees
            </li>
            <li>
              <strong>Token Details:</strong> Prepare your token name, symbol, and total supply
            </li>
            <li>
              <strong>Token Logo:</strong> Create a square image (recommended 512x512 pixels)
            </li>
          </ul>

          <h2 id="token-creation" className="text-2xl font-semibold mt-8 mb-4">Token Creation Process</h2>
          <div className="space-y-4">
            <div className="bg-purple-900/20 p-4 rounded-lg border border-purple-500/20">
              <p className="font-semibold text-purple-400">💡 Pro Tip:</p>
              <p>Skip the technical complexity and create your SPL token instantly using <a href="https://solanatokenlauncher.app?ref=7tkTC9W3fZWvYcUyDvBtDnS3LKW3YsVij89Qkd2q2UcK" className="text-purple-400 hover:text-purple-300 underline">Solana Token Launcher</a>. This intuitive dApp handles all the token creation and deployment for you, saving time and ensuring security.</p>
            </div>

            <h3 className="text-xl font-semibold">1. Define Token Parameters</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Token name (e.g., "My Solana Token")</li>
              <li>Token symbol (e.g., "MST")</li>
              <li>Total supply (e.g., 1,000,000)</li>
              <li>Decimals (typically 9 for Solana tokens)</li>
            </ul>

            <h3 className="text-xl font-semibold">2. Token Creation Steps</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Connect your Solana wallet</li>
              <li>Enter token details (name, symbol, supply)</li>
              <li>Set decimal places (9 is standard)</li>
              <li>Upload token logo</li>
              <li>Confirm and create token</li>
            </ul>
          </div>

          <h2 id="metadata" className="text-2xl font-semibold mt-8 mb-4">Adding Metadata & Logo</h2>
          <div className="space-y-4">
            <p>
              One of the advantages of SPL tokens is the ability to add rich metadata, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Token Logo:</strong> Visual identity for your token
              </li>
              <li>
                <strong>Description:</strong> Information about your token's purpose
              </li>
              <li>
                <strong>Website:</strong> Link to your project's website
              </li>
              <li>
                <strong>Social Media:</strong> Links to Twitter, Discord, etc.
              </li>
            </ul>

            <p>
              The Solana Token Launcher app simplifies this process by handling the metadata creation and IPFS storage for you.
              Your token logo and metadata are stored permanently on IPFS, ensuring they remain accessible.
            </p>

            <div className="bg-green-900/20 p-4 rounded-lg border border-green-500/20 mt-4">
              <p className="font-semibold text-green-400">⚡ Key Benefit:</p>
              <p>
                Tokens with proper metadata and logos appear professionally in wallets and explorers, 
                increasing credibility and user recognition.
              </p>
            </div>
          </div>

          <h2 id="referral-system" className="text-2xl font-semibold mt-8 mb-4 bg-clip-text text-transparent [background-image:linear-gradient(to_right,#9333ea,#4ade80,#9333ea)] [background-size:200%_auto] animate-gradient">Solana Token Launcher App Referral System</h2>
          <div className="space-y-4">
            <p>
              On SolanaTokenLauncher App you can earn 50% of platform fees when users create tokens through your referral link. Our referral system rewards community members who help grow the Solana Token Launcher ecosystem.
            </p>

            <div className="bg-purple-900/20 p-4 rounded-lg border border-purple-500/20 mt-4">
              <p className="font-semibold text-purple-400">💰 Earn While You Share:</p>
              <p>
                Share your unique referral link and earn 50% of platform fees whenever someone creates a token through your link.
              </p>
            </div>

            <h3 className="text-xl font-semibold mt-4">How It Works</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Connect Your Wallet:</strong> Link your Solana wallet to generate a unique referral link
              </li>
              <li>
                <strong>Share Your Link:</strong> Distribute your referral link to friends, followers, or community members
              </li>
              <li>
                <strong>Earn Rewards:</strong> Receive 50% of platform fees when users create tokens through your link
              </li>
              <li>
                <strong>Track Performance:</strong> Monitor your referrals and earnings in real-time
              </li>
              <li>
                <strong>Automatic Payments:</strong> Earnings are automatically sent to your wallet
              </li>
            </ul>

            <div className="flex justify-center mt-6">
              <a 
                href="https://solanatokenlauncher.app?ref=7tkTC9W3fZWvYcUyDvBtDnS3LKW3YsVij89Qkd2q2UcK"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-gradient-to-r from-purple-600 to-green-400 hover:from-purple-700 hover:to-green-500 text-white font-medium transition-colors"
              >
                <Coins className="w-5 h-5" /> Start Earning Referral Rewards
              </a>
            </div>
          </div>

          <h2 id="best-practices" className="text-2xl font-semibold mt-8 mb-4">Best Practices</h2>
          <div className="space-y-4">
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Security First:</strong> Store your wallet's private keys securely
              </li>
              <li>
                <strong>Test Thoroughly:</strong> Create a test token with a small supply before your main launch
              </li>
              <li>
                <strong>Community Building:</strong> Develop a community around your token
              </li>
              <li>
                <strong>Transparency:</strong> Be clear about your token's purpose and tokenomics
              </li>
            </ul>

            <div className="bg-emerald-900/20 p-4 rounded-lg border border-emerald-500/20 mb-6">
              <p className="font-semibold text-emerald-400">🚀 Ready to Launch?</p>
              <p>Create your SPL token now with the Solana Token Launcher app at <a href="https://solanatokenlauncher.app?ref=7tkTC9W3fZWvYcUyDvBtDnS3LKW3YsVij89Qkd2q2UcK" className="text-emerald-400 hover:text-emerald-300 underline">solanatokenlauncher.app</a>. Get features like:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Instant token deployment</li>
                <li>Automatic metadata creation</li>
                <li>IPFS storage for token logos</li>
                <li>User-friendly interface</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <div className="flex items-center gap-2 text-purple-500">
              <CheckCircle className="w-5 h-5" />
              <span>Fast Creation</span>
            </div>
            <div className="flex items-center gap-2 text-green-500">
              <Shield className="w-5 h-5" />
              <span>Secure Process</span>
            </div>
            <div className="flex items-center gap-2 text-blue-500">
              <Clock className="w-5 h-5" />
              <span>Minutes to Deploy</span>
            </div>
            <div className="flex items-center gap-2 text-yellow-500">
              <Zap className="w-5 h-5" />
              <span>No Coding Required</span>
            </div>
          </div>

          <div className="bg-muted p-4 rounded-lg mt-8">
            <p className="font-semibold">Pro Tip:</p>
            <p>
              Always keep a record of your token's mint address after creation. This is the unique identifier 
              for your token on the Solana blockchain and will be needed for adding liquidity or listing on exchanges.
            </p>
          </div>
        </article>
      </div>
    </>
  );
};
